import {
  FunctionComponent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from 'react'

import styled, { css, useTheme } from 'styled-components'

import { ArrowsVisibilityType, SwiperClass } from '@hooks/useSwiper'
import IconButton from '@components/Buttons/IconButton'

export interface SwiperArrowsProps {
  swiperRef: React.RefObject<SwiperClass | null>
  arrowsVisibility?: ArrowsVisibilityType
  className?: string
}

const ArrowWrapper = styled.div<{
  direction: 'left' | 'right'
  isHidden: boolean
}>`
  ${({
    theme: {
      colors: { staticBlack },
      utils: { opacity },
    },
    direction,
    isHidden,
  }) => css`
    width: 160px;
    height: 100%;
    position: absolute;
    top: 0;
    display: flex;
    align-items: center;

    ${direction}: 0;
    ${'padding-' + direction}: 24px;
    background: linear-gradient(
      to ${direction},
      ${opacity(staticBlack, '00')},
      ${staticBlack}
    );

    ${direction === 'left' &&
    css`
      justify-content: flex-start;
    `}

    ${direction === 'right' &&
    css`
      justify-content: flex-end;
    `}

    ${isHidden &&
    css`
      display: none;
    `}
  `}
`
const StyledWrapper = styled.div``

const SwiperArrows: FunctionComponent<SwiperArrowsProps> = ({
  className,
  arrowsVisibility = 'next',
  swiperRef,
}) => {
  const theme = useTheme()

  const [isLeftArrowHidden, setLeftArrowHidden] = useState<boolean>(() =>
    ['none', 'next'].includes(arrowsVisibility),
  )
  const [isRightArrowHidden, setRightArrowHidden] = useState<boolean>(() =>
    ['none', 'prev'].includes(arrowsVisibility),
  )

  useEffect(() => {
    setLeftArrowHidden(['none', 'next'].includes(arrowsVisibility))
    setRightArrowHidden(['none', 'prev'].includes(arrowsVisibility))
  }, [arrowsVisibility])

  const onLeftArrowClick = useCallback(
    (e?: SyntheticEvent) => {
      e?.preventDefault()
      swiperRef.current?.slideToDirection('prev')
    },
    [swiperRef],
  )

  const onRightArrownClick = useCallback(
    (e?: SyntheticEvent) => {
      e?.preventDefault()
      swiperRef.current?.slideToDirection('next')
    },
    [swiperRef],
  )

  return (
    <StyledWrapper className={className}>
      <ArrowWrapper direction="left" isHidden={isLeftArrowHidden}>
        <IconButton
          iconName="chevron-left"
          iconSize={32}
          iconColor={theme.colors.staticWhite}
          buttonSize={32}
          onClick={onLeftArrowClick}
          ariaLabel="left arrow"
        />
      </ArrowWrapper>
      <ArrowWrapper direction="right" isHidden={isRightArrowHidden}>
        <IconButton
          iconName="chevron-right"
          iconSize={32}
          iconColor={theme.colors.staticWhite}
          buttonSize={32}
          onClick={onRightArrownClick}
          ariaLabel="right arrow"
        />
      </ArrowWrapper>
    </StyledWrapper>
  )
}

export default SwiperArrows
